import React, {
  RefObject,
  useEffect,
  useMemo,
} from 'react';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  useSidePanel,
  useTableReset,
} from 'core/hooks';
import InvoiceIcon from 'core/icons/invoice';
import PaymentSuccessIcon from 'core/icons/paymentSuccess';
import DollarCircleIcon from 'core/icons/dollarCircle';
import {
  useGetLedgerData,
} from 'pages/Dashboard/pages/Ledger/hooks';
import {
  Props as ChartComponentProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/types/tabContentProps';
import Spinner from 'pages/Dashboard/components/Spinner';
import ToolTip from 'core/components/Tooltip';
import IconButton from '@mui/material/IconButton';
import PrinterIcon from 'core/icons/printer';
import MailIcon from '@mui/icons-material/MailOutlineRounded';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import {
  Patient,
} from 'pages/Dashboard/types/patient';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import DepositIcon from 'core/icons/deposit';
import Button from 'pages/Dashboard/components/Button';
import Divider from '@mui/material/Divider';
import PaymentRemoveIcon from 'core/icons/paymentRemove';
import Overview from 'pages/Dashboard/pages/Ledger/Overview';
import map from 'lodash/map';
import compact from 'lodash/compact';
import AmountSummary, {
  AmountSummaryItem,
} from 'views/EMR/Billing/AmountSummary';
import clsx from 'clsx';
import FinalNoticeButton from 'pages/Dashboard/pages/Ledger/components/FinalNoticeButton';
import {
  isNilOrZero,
} from '@populateui/utils';

const summaryItems: AmountSummaryItem[] = [{
  Icon: InvoiceIcon,
  title: 'Net Charges',
  variant: 'netCharges',
}, {
  Icon: PaymentSuccessIcon,
  title: 'Insurance Paid',
  variant: 'insurancePaid',
}, {
  Icon: DollarCircleIcon,
  title: 'Insurance Balance',
  variant: 'insuranceBalance',
}, {
  Icon: PaymentSuccessIcon,
  title: 'Patient Paid',
  variant: 'patientPaid',
}, {
  Icon: DollarCircleIcon,
  title: 'Patient\'s Balance',
  variant: 'patientBalance',
}, {
  Icon: PaymentRemoveIcon,
  title: 'Total balance overdue',
  variant: 'patientDue',
}, {
  Icon: DepositIcon,
  title: 'Patient\'s Credit',
  variant: 'patientCredit',
}];

const iconClass = '!w-5 !h-5 !text-gray-400';

export default function LedgerView({ patient }: Partial<ChartComponentProps>) {
  const { tableInstanceRef } = useTableReset();
  const { openPanel } = useSidePanel();
  const {
    data: ledgerData,
    isLoading,
    refetch,
  } = useGetLedgerData(patient?.patientId ?? 0);

  const { ledgers, summaryLedger } = ledgerData ?? {};

  const visits = useMemo(
    () => compact(map(ledgers ?? [], 'encounterId')),
    [ledgers],
  );

  const statementDisabled = visits?.length === 0;
  const patientDue = summaryLedger?.patientDue ?? 0;
  const finalNoticeDisabled = isNilOrZero(patient?.patientId) || isNilOrZero(patientDue);
  const finalNoticeDisabledReason = isNilOrZero(patient?.patientId)
    ? 'Patient is missing'
    : 'No balance due for this patient';

  useEffect(() => {
    const unsubscribe = paymentsEventChannel.on('onPaymentsDataChanged', () => {
      refetch();
    });
    return () => { unsubscribe(); };
  }, []);

  return isLoading
    ? <div className="flex items-center justify-center w-full h-full"><Spinner size={36} /></div>
    : (
      <div className="flex h-full min-h-0 w-full flex-col gap-4 overflow-y-auto">
        <div className="flex gap-4 w-full px-4 mt-1 flex-wrap">
          {summaryItems.map((item) => (
            <AmountSummary
              key={item.variant}
              {...item}
              value={summaryLedger?.[item.variant]}
            />
          ))}
        </div>
        <div className="flex justify-end gap-3 items-center px-4">
          <div className="flex items-center">
            <ToolTip
              title={statementDisabled
                ? 'No Statement available for this patient'
                : 'Print Patient Statement'}
            >
              <span>
                <IconButton
                  aria-label="Print Patient Statement"
                  color="info"
                  onClick={() => {
                    paymentsEventChannel.emit('onStatementDialog', {
                      type: 'print',
                      patient: patient as Patient,
                      visits,
                      excludeEncountersWithNoDues: true,
                    });
                  }}
                  disabled={statementDisabled}
                  className="disabled:opacity-50"
                >
                  <PrinterIcon className={iconClass} />
                </IconButton>
              </span>
            </ToolTip>
            <ToolTip
              title={statementDisabled
                ? 'No Statement available for this patient'
                : 'Send Patient Statement'}
            >
              <span>
                <IconButton
                  aria-label="Send Patient Statement"
                  color="info"
                  onClick={() => {
                    paymentsEventChannel.emit('onStatementDialog', {
                      type: 'send',
                      patient: patient as Patient,
                      visits,
                      excludeEncountersWithNoDues: true,
                    });
                  }}
                  disabled={statementDisabled}
                  className="disabled:opacity-50"
                >
                  <MailIcon className={iconClass} />
                </IconButton>
              </span>
            </ToolTip>
            <ToolTip title="Print Financial History">
              <span>
                <IconButton
                  aria-label="Print Financial History"
                  color="info"
                  onClick={() => {
                    paymentsEventChannel.emit('onFinancialHistoryDialog', {
                      patientId: patient?.patientId ?? 0,
                    });
                  }}
                  disabled={isNilOrZero(patient?.patientId)}
                  className="disabled:opacity-50"
                >
                  <ReceiptLongIcon className={iconClass} />
                </IconButton>
              </span>
            </ToolTip>
          </div>
          <Divider orientation="vertical" variant="middle" flexItem className="!mr-1" />
          <ToolTip
            title={finalNoticeDisabled ? finalNoticeDisabledReason : ''}
            hide={!finalNoticeDisabled}
          >
            <span
              className={clsx({
                'cursor-not-allowed': finalNoticeDisabled,
              })}
            >
              <FinalNoticeButton
                disabled={finalNoticeDisabled}
                onPrint={() => {
                  paymentsEventChannel.emit('onFinalNoticeDialog', {
                    type: 'print',
                    patient: patient as Patient,
                    patientDue,
                  });
                }}
                onSend={() => {
                  paymentsEventChannel.emit('onFinalNoticeDialog', {
                    type: 'send',
                    patient: patient as Patient,
                    patientDue,
                  });
                }}
              />
            </span>
          </ToolTip>
          <Button
            disabled={statementDisabled}
            variant="outlined"
            onClick={() => {
              openPanel('statementHistory', { patientId: patient?.patientId ?? 0 });
            }}
            className="!h-8 disabled:opacity-50 disabled:!pointer-events-auto disabled:!cursor-not-allowed"
            startIcon={<HistoryIcon className="!w-4 !h-4 !text-inherit" />}
          >
            Statement History
          </Button>
        </div>
        <Overview
          tableInstanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
          ledgers={ledgers ?? []}
        />
      </div>
    );
}
